import { useState, useEffect } from "react";
import { loadBibleData, BibleBook } from "@/lib/bible-api";

interface UseBibleDataResult {
  books: Record<string, BibleBook>;
  allBookNames: string[];
  isLoading: boolean;
  error: string | null;
  getBookChapters: (bookName: string) => number;
  getChapterVerses: (bookName: string, chapterNum: number) => number;
}

export function useBibleData(): UseBibleDataResult {
  const [books, setBooks] = useState<Record<string, BibleBook>>({});
  const [allBookNames, setAllBookNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBibleData() {
      try {
        setIsLoading(true);
        const data = await loadBibleData();
        setBooks(data);
        
        const bookNames = Object.keys(data).sort((a, b) => {
          // Sort by book order (Genesis first, Revelation last)
          const oldTestament = [
            "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", 
            "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel", "1 Kings", 
            "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", 
            "Esther", "Job", "Psalms", "Proverbs", "Ecclesiastes", 
            "Song of Solomon", "Isaiah", "Jeremiah", "Lamentations", 
            "Ezekiel", "Daniel", "Hosea", "Joel", "Amos", "Obadiah", 
            "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", 
            "Zechariah", "Malachi"
          ];
          
          const newTestament = [
            "Matthew", "Mark", "Luke", "John", "Acts", "Romans", 
            "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians", 
            "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians", 
            "1 Timothy", "2 Timothy", "Titus", "Philemon", "Hebrews", 
            "James", "1 Peter", "2 Peter", "1 John", "2 John", "3 John", 
            "Jude", "Revelation"
          ];
          
          const canonicalOrder = [...oldTestament, ...newTestament];
          return canonicalOrder.indexOf(a) - canonicalOrder.indexOf(b);
        });
        
        setAllBookNames(bookNames);
        setError(null);
      } catch (err) {
        console.error("Error loading Bible data:", err);
        setError("Failed to load Bible data");
      } finally {
        setIsLoading(false);
      }
    }

    fetchBibleData();
  }, []);

  const getBookChapters = (bookName: string): number => {
    if (!books[bookName]) return 0;
    return books[bookName].no_of_chapters;
  };

  const getChapterVerses = (bookName: string, chapterNum: number): number => {
    if (!books[bookName] || chapterNum < 1 || chapterNum > books[bookName].no_of_chapters) {
      return 0;
    }
    
    return books[bookName].chapters_to_number_of_verses[chapterNum - 1];
  };

  return {
    books,
    allBookNames,
    isLoading,
    error,
    getBookChapters,
    getChapterVerses,
  };
}